import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Package,
  Car,
  AlertTriangle,
  CheckCircle2,
  Send,
  Building2,
  Phone,
  Mail,
  Clock,
  Wrench,
} from "lucide-react";
import AutocompleteInput from "../components/AutocompleteInput";
import { autoPartsDescriptions } from "../data/autoParts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const urgencyLabels = {
  standard: "Standard",
  morning: "URGENTE - In Mattinata",
  evening: "URGENTE - In Serata",
  today: "URGENTE - Consegna oggi",
  vehicle_down: "FERMO MACCHINA",
};

const urgencyColors = {
  standard: "bg-zinc-700 text-zinc-300",
  morning: "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50",
  evening: "bg-amber-500/20 text-amber-400 border border-amber-500/50",
  today: "bg-orange-500/20 text-orange-400 border border-orange-500/50",
  vehicle_down: "bg-red-500/20 text-red-400 border border-red-500/50",
};

export default function SupplierResponse() {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [responses, setResponses] = useState([]);
  const [notes, setNotes] = useState("");
  const [contactName, setContactName] = useState("");

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/parts-request/${token}`);
        if (res.ok) {
          const data = await res.json();
          setRequest(data);

          // Initialize responses for each part
          setResponses(
            data.parts.map((part) => ({
              part_id: part.part_id,
              available: false,
              price: 0,
              code: part.code || "",  // Codice ricambio - OBBLIGATORIO
              brand: "",              // Marca ricambio - OBBLIGATORIO
              brand_type: "",
              delivery_time: "",
              alternative_code: "",
              alternative_description: "",
              alternative_brand: "",  // Marca alternativo - OBBLIGATORIO se non disponibile
              alternative_quantity: 0,
              alternative_price: 0,
              notes: "",
            }))
          );

          // Check if already responded
          if (data.status === "responded" || data.status === "accepted") {
            setSubmitted(true);
          }
        } else {
          setError("Richiesta non trovata o link scaduto");
        }
      } catch (err) {
        setError("Errore di connessione");
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [token]);

  const updateResponse = (partId, field, value) => {
    setResponses((prev) =>
      prev.map((r) => (r.part_id === partId ? { ...r, [field]: value } : r))
    );
  };

  const handleSubmit = async () => {
    // Validate at least one response
    const hasResponse = responses.some(
      (r) => r.available || r.alternative_description
    );
    if (!hasResponse) {
      toast.error("Indica almeno una disponibilità o alternativa");
      return;
    }

    // Validate required fields for available items
    const availableResponses = responses.filter(r => r.available);
    for (const r of availableResponses) {
      if (!r.code || !r.code.trim()) {
        toast.error("Il campo 'Codice Ricambio' è obbligatorio per i ricambi disponibili");
        return;
      }
      if (!r.brand || !r.brand.trim()) {
        toast.error("Il campo 'Marca' è obbligatorio per i ricambi disponibili");
        return;
      }
    }

    // Validate required fields for alternative items (when not available but has alternative)
    const alternativeResponses = responses.filter(r => !r.available && r.alternative_description);
    for (const r of alternativeResponses) {
      if (!r.alternative_code || !r.alternative_code.trim()) {
        toast.error("Il campo 'Codice' è obbligatorio per gli articoli alternativi");
        return;
      }
      if (!r.alternative_brand || !r.alternative_brand.trim()) {
        toast.error("Il campo 'Marca' è obbligatorio per gli articoli alternativi");
        return;
      }
    }

    setSubmitting(true);

    try {
      const res = await fetch(
        `${API_URL}/api/public/parts-request/${token}/respond`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            responses,
            notes,
            supplier_contact_name: contactName,
          }),
        }
      );

      if (res.ok) {
        setSubmitted(true);
        toast.success("Risposta inviata con successo!");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore nell'invio della risposta");
      }
    } catch (err) {
      toast.error("Errore di connessione");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Errore</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Grazie!</h1>
          <p className="text-zinc-400 mb-6">
            La tua risposta è stata inviata con successo all'officina.
          </p>
          <div className="p-4 bg-zinc-800/50 rounded-lg">
            <p className="text-sm text-zinc-400">
              Richiesta #{request?.request_number}
            </p>
            <p className="text-white font-semibold">{request?.workshop_name}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="supplier-response-page">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                Richiesta Ricambi #{request?.request_number}
              </h1>
              <p className="text-sm text-zinc-400">
                Da: {request?.workshop_name}
              </p>
            </div>
          </div>

          {/* Urgency Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
              urgencyColors[request?.urgency]
            }`}
          >
            {request?.urgency === "vehicle_down" && (
              <AlertTriangle className="w-5 h-5" />
            )}
            {request?.urgency === "today" && <Clock className="w-5 h-5" />}
            <span className="font-semibold">
              {urgencyLabels[request?.urgency]}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Workshop Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-zinc-500" />
            <div>
              <div className="text-xs text-zinc-500">Officina</div>
              <div className="text-white">{request?.workshop_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-zinc-500" />
            <div>
              <div className="text-xs text-zinc-500">Telefono</div>
              <div className="text-white">{request?.workshop_phone || "-"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-zinc-500" />
            <div>
              <div className="text-xs text-zinc-500">Email</div>
              <div className="text-white">{request?.workshop_email || "-"}</div>
            </div>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">Dati Veicolo</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-zinc-500">Marca</div>
              <div className="text-white font-medium">
                {request?.vehicle?.brand}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Modello</div>
              <div className="text-white font-medium">
                {request?.vehicle?.model}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Versione</div>
              <div className="text-white">
                {request?.vehicle?.version || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Anno</div>
              <div className="text-white">{request?.vehicle?.year || "-"}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Targa</div>
              <div className="text-white font-mono">
                {request?.vehicle?.plate || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Codice Motore</div>
              <div className="text-white font-mono">
                {request?.vehicle?.engine_code || "-"}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs text-zinc-500">VIN / Telaio</div>
              <div className="text-white font-mono text-sm">
                {request?.vehicle?.vin || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Parts - Response Form */}
        <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 mb-4">
            <Wrench className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-white">
              Ricambi Richiesti ({request?.parts?.length})
            </h2>
          </div>
          <p className="text-sm text-zinc-400 mb-6">
            Compila la disponibilità e i prezzi per ogni ricambio. Puoi anche
            proporre alternative.
          </p>

          <div className="space-y-6">
            {request?.parts?.map((part, index) => {
              const response = responses.find((r) => r.part_id === part.part_id);

              return (
                <div
                  key={part.part_id}
                  className={`p-4 rounded-lg border ${
                    response?.available
                      ? "bg-green-500/5 border-green-500/30"
                      : "bg-zinc-800/50 border-zinc-700"
                  }`}
                >
                  {/* Part Info */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-white font-semibold text-lg">
                        {index + 1}. {part.description}
                      </div>
                      {part.code && (
                        <div className="text-sm text-zinc-400 font-mono">
                          Codice: {part.code}
                        </div>
                      )}
                      <div className="text-sm text-zinc-500">
                        Quantità richiesta: {part.quantity}
                      </div>
                    </div>
                  </div>

                  {/* Availability Checkbox */}
                  <div className="flex items-center gap-3 mb-4 p-3 bg-zinc-800/50 rounded-lg">
                    <Checkbox
                      id={`avail-${part.part_id}`}
                      checked={response?.available || false}
                      onCheckedChange={(checked) =>
                        updateResponse(part.part_id, "available", checked)
                      }
                      className="h-5 w-5"
                    />
                    <Label
                      htmlFor={`avail-${part.part_id}`}
                      className="text-white cursor-pointer"
                    >
                      Disponibile
                    </Label>
                  </div>

                  {/* Price and Details (show if available) */}
                  {response?.available && (
                    <div className="space-y-4 mb-4">
                      {/* Codice e Marca OBBLIGATORI */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400">Codice Ricambio *</Label>
                          <Input
                            type="text"
                            value={response?.code || ""}
                            onChange={(e) =>
                              updateResponse(part.part_id, "code", e.target.value.toUpperCase())
                            }
                            placeholder="Es: 1K0615301M"
                            className="bg-zinc-800 uppercase"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400">Marca *</Label>
                          <Input
                            type="text"
                            value={response?.brand || ""}
                            onChange={(e) =>
                              updateResponse(part.part_id, "brand", e.target.value)
                            }
                            placeholder="Es: Brembo, TRW, Bosch..."
                            className="bg-zinc-800"
                            required
                          />
                        </div>
                      </div>

                      {/* Prezzo, Tipo, Consegna */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-zinc-400">Prezzo unitario €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={response?.price || ""}
                            onChange={(e) =>
                              updateResponse(
                                part.part_id,
                                "price",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            placeholder="0.00"
                            className="bg-zinc-800"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400">Tipo</Label>
                          <Select
                            value={response?.brand_type || ""}
                            onValueChange={(value) =>
                              updateResponse(part.part_id, "brand_type", value)
                            }
                          >
                          <SelectTrigger className="bg-zinc-800">
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OEM">OEM (Originale)</SelectItem>
                            <SelectItem value="aftermarket">Aftermarket</SelectItem>
                            <SelectItem value="rigenerato">Rigenerato</SelectItem>
                          </SelectContent>
                        </Select>
                        </div>
                        <div>
                          <Label className="text-zinc-400">Consegna</Label>
                          <Select
                            value={response?.delivery_time || ""}
                            onValueChange={(value) =>
                              updateResponse(part.part_id, "delivery_time", value)
                            }
                          >
                            <SelectTrigger className="bg-zinc-800">
                              <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="immediata">Immediata</SelectItem>
                              <SelectItem value="in mattinata">In Mattinata</SelectItem>
                              <SelectItem value="in serata">In Serata</SelectItem>
                              <SelectItem value="oggi">Consegna Oggi</SelectItem>
                              <SelectItem value="24h">24 ore</SelectItem>
                              <SelectItem value="48h">48 ore</SelectItem>
                              <SelectItem value="3-5gg">3-5 giorni</SelectItem>
                              <SelectItem value="1 settimana">1 settimana</SelectItem>
                              <SelectItem value="su ordinazione">Su ordinazione</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-zinc-400">Note</Label>
                          <Input
                            value={response?.notes || ""}
                            onChange={(e) =>
                              updateResponse(part.part_id, "notes", e.target.value)
                            }
                            placeholder="Note..."
                            className="bg-zinc-800"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alternative Part */}
                  <div className="border-t border-zinc-700 pt-4 mt-4">
                    <Label className="text-zinc-400 text-sm mb-2 block">
                      {response?.available 
                        ? "Proponi un ricambio alternativo (opzionale)"
                        : "Ricambio alternativo (obbligatorio se non disponibile)"
                      }
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Codice {!response?.available && "*"}
                        </Label>
                        <Input
                          value={response?.alternative_code || ""}
                          onChange={(e) =>
                            updateResponse(
                              part.part_id,
                              "alternative_code",
                              e.target.value.toUpperCase()
                            )
                          }
                          placeholder="Codice"
                          className={`bg-zinc-800 uppercase ${!response?.available && !response?.alternative_code ? 'border-orange-500/50' : ''}`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-zinc-500">
                          Descrizione {!response?.available && "*"}
                        </Label>
                        <AutocompleteInput
                          value={response?.alternative_description || ""}
                          onChange={(val) =>
                            updateResponse(
                              part.part_id,
                              "alternative_description",
                              val
                            )
                          }
                          suggestions={autoPartsDescriptions}
                          placeholder="Descrizione"
                          inputClassName={`bg-zinc-800 ${!response?.available && !response?.alternative_description ? 'border-orange-500/50' : ''}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Marca {!response?.available && "*"}
                        </Label>
                        <Input
                          value={response?.alternative_brand || ""}
                          onChange={(e) =>
                            updateResponse(
                              part.part_id,
                              "alternative_brand",
                              e.target.value
                            )
                          }
                          placeholder="Marca"
                          className={`bg-zinc-800 ${!response?.available && !response?.alternative_brand ? 'border-orange-500/50' : ''}`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-zinc-500">Qtà</Label>
                          <Input
                            type="number"
                            min="1"
                            value={response?.alternative_quantity || ""}
                            onChange={(e) =>
                              updateResponse(
                                part.part_id,
                                "alternative_quantity",
                                parseInt(e.target.value) || 0
                              )
                            }
                            placeholder="1"
                            className="bg-zinc-800"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">€</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={response?.alternative_price || ""}
                            onChange={(e) =>
                              updateResponse(
                                part.part_id,
                                "alternative_price",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            placeholder="0.00"
                            className="bg-zinc-800"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* General Notes */}
        <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <Label className="text-zinc-400 mb-2 block">Note generali</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Eventuali note aggiuntive per l'officina..."
            rows={3}
            className="bg-zinc-800"
          />
        </div>

        {/* Contact Name */}
        <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <Label className="text-zinc-400 mb-2 block">
            Il tuo nome (opzionale)
          </Label>
          <Input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Es: Mario Rossi"
            className="bg-zinc-800 max-w-md"
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-center py-6">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            size="lg"
            className="gap-2 px-8 py-6 text-lg"
            data-testid="submit-response-btn"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Invia Risposta
              </>
            )}
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-zinc-500 text-sm pb-8">
          <p>Powered by PServicePro - Gestione Officina</p>
        </div>
      </div>
    </div>
  );
}
